import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LayoutGrid, PlusCircle, LogOut, Moon, Sun, ShieldCheck, KeyRound } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const NavItem = ({ to, icon: Icon, label, active, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`flex items-center gap-3 px-4 py-3 border-l-2 transition-colors duration-150 text-sm font-medium ${
      active
        ? "border-primary bg-muted text-foreground"
        : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"
    }`}
  >
    <Icon size={18} strokeWidth={2} />
    {label}
  </Link>
);

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const [pwOpen, setPwOpen] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password berhasil diubah");
      setPwOpen(false);
      setOldPw("");
      setNewPw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gagal mengubah password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 border-r border-border flex flex-col fixed h-screen bg-background z-20">
        <div className="px-6 py-6 border-b border-border">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div>
              <div className="font-heading font-black text-lg tracking-tight leading-none">Minutes System</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 py-4 flex flex-col gap-1">
          <NavItem to="/" icon={LayoutGrid} label="Meeting History" active={path === "/"} testid="nav-history" />
          <NavItem to="/new" icon={PlusCircle} label="New Meeting" active={path === "/new"} testid="nav-new" />
          {isAdmin && (
            <NavItem to="/admin" icon={ShieldCheck} label="Control Panel" active={path === "/admin"} testid="nav-admin" />
          )}
        </nav>

        <div className="border-t border-border p-4 flex flex-col gap-3">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="flex items-center gap-3 px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
          <button
            onClick={() => setPwOpen(true)}
            data-testid="change-password-btn"
            className="flex items-center gap-3 px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            <KeyRound size={18} />
            Ganti Password
          </button>
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate" data-testid="current-user-scope">
                {isAdmin ? "Super Admin" : (user?.department || "—")}
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 text-muted-foreground hover:text-primary transition-colors duration-150"
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen">{children}</main>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="rounded-none" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Ganti Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={changePassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="oldpw">Password Lama</Label>
              <Input
                id="oldpw"
                type="password"
                data-testid="old-password-input"
                value={oldPw}
                onChange={(e) => setOldPw(e.target.value)}
                className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="newpw">Password Baru (min. 6 karakter)</Label>
              <Input
                id="newpw"
                type="password"
                data-testid="new-password-input"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={saving}
                data-testid="save-password-btn"
                className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {saving ? "Menyimpan…" : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
